import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { planDocMove } from '../utils/docMove'
import type { DocLocationPatch, DocMoveTarget } from '../utils/docMove'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { usePageMutations } from '@/lib/db/rxdb/hooks'
import { getReplicationState } from '@/lib/db/rxdb'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

/** How long to wait for the moved doc to reach the server before its sub-pages. */
const PUSH_WAIT_MS = 2500

/**
 * The server checks that a page's parent lives in the same project, against
 * what it has already stored. Pushing the moved doc before its sub-pages keeps
 * that check happy when it runs per batch; offline, both simply queue.
 */
async function waitForPagesPush(): Promise<void> {
  const state: { awaitInSync?: () => Promise<unknown> } | undefined = getReplicationState('pages')
  if (!state?.awaitInSync) return
  await Promise.race([
    state.awaitInSync().catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, PUSH_WAIT_MS)),
  ])
}

export interface MoveDocOptions {
  /** Name of the destination, for the toast ("Moved to Zyro"). */
  destinationName: string
  /** Called with the moved doc's new location, after the move and after an undo. */
  onMoved?: (location: DocLocationPatch) => void
}

/**
 * Move a doc — and every sub-page under it — to a space, project or parent
 * doc (design 22b). Task links and backlinks are keyed by page id, so they
 * keep working. Shows a toast with Undo.
 */
export function useMoveDoc(pages: ReadonlyArray<PageDocument>) {
  const { t } = useTranslation('projects')
  const { updatePage } = usePageMutations()

  const apply = useCallback(
    async (patches: ReadonlyArray<DocLocationPatch>) => {
      const [root, ...rest] = patches
      if (!root) return
      const { id, ...fields } = root
      await updatePage(id, fields)
      if (rest.length === 0) return
      await waitForPagesPush()
      for (const patch of rest) {
        const { id: childId, ...childFields } = patch
        await updatePage(childId, childFields)
      }
    },
    [updatePage],
  )

  return useCallback(
    async (docId: string, target: DocMoveTarget, options: MoveDocOptions): Promise<boolean> => {
      const plan = planDocMove(pages, docId, target)
      if (!plan.ok) {
        if (plan.reason === 'depth') toast.error(t('docs.move.tooDeep'))
        else if (plan.reason === 'cycle') toast.error(t('docs.move.cycle'))
        return false
      }
      try {
        await apply(plan.updates)
      } catch (error) {
        logger.logError(error as Error, LogCategory.DATABASE, { operation: 'moveDoc' })
        toast.error(t('docs.move.failed'))
        return false
      }
      options.onMoved?.(plan.updates[0]!)
      toast.success(t('docs.move.moved', { name: options.destinationName }), {
        action: {
          label: t('docs.move.undo'),
          onClick: () => {
            void apply(plan.previous)
              .then(() => options.onMoved?.(plan.previous[0]!))
              .catch((error: unknown) => {
                logger.logError(error as Error, LogCategory.DATABASE, { operation: 'undoMoveDoc' })
                toast.error(t('docs.move.failed'))
              })
          },
        },
      })
      return true
    },
    [pages, apply, t],
  )
}
